"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  CheckCircle2,
  KeyRound,
  LoaderCircle,
  LogOut,
  MoreVertical,
  Search,
  ShieldCheck,
  UserRoundCog,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SetupAccount = {
  email: string;
  user_id?: string | null;
  role: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  active: boolean;
  joined: boolean;
  project_ref?: string | null;
  project_status?: string | null;
  observed_schema_version?: string | null;
  verified_schema_version?: string | null;
  activation_state: string;
  setup_stage: string;
  central_otp_sent_at?: string | null;
  central_otp_verified_at?: string | null;
  member_otp_sent_at?: string | null;
  member_otp_verified_at?: string | null;
  activation_completed_at?: string | null;
  last_failure?: string | null;
  first_use_validation?: Record<string, unknown>;
  permissions?: Record<string, boolean>;
};

type SetupSession = {
  setup_session_id: string;
  target_email: string;
  target_user_id?: string | null;
  target_project_ref?: string | null;
  status: string;
  started_at: string;
  expires_at: string;
  last_activity_at: string;
};

type SupportSession = {
  active: boolean;
  support_session_id?: string;
  actor_email?: string;
  actor_role?: string;
  target_email?: string;
  target_user_id?: string | null;
  tenant_project_ref?: string | null;
  support_mode?: string;
  reason?: string;
  started_at?: string;
  expires_at?: string;
  banner?: string;
  non_impersonating?: boolean;
};

async function readAccountSetupError(error: unknown, data: unknown) {
  let raw = "";

  if (data && typeof data === "object") {
    const payload = data as Record<string, unknown>;
    raw = String(payload.detail ?? payload.error ?? "").trim();
  }

  const context = (error as { context?: unknown } | null)?.context;
  if (!raw && context && typeof context === "object") {
    const response = context as Response;
    if (typeof response.clone === "function") {
      try {
        const payload = await response.clone().json() as Record<string, unknown>;
        raw = String(payload?.detail ?? payload?.error ?? "").trim();
      } catch {
        try {
          raw = (await response.clone().text()).trim();
        } catch {
          // Preserve the normal FunctionsHttpError fallback below.
        }
      }
    }
  }

  if (!raw && error instanceof Error) raw = error.message;
  if (!raw) raw = "RUNYI account verification failed.";

  if (raw.includes("CONFIRMATION_LINK_TRACKING_HOST_NOT_ALLOWED")) {
    return "This email link uses an unapproved tracking domain. Send a fresh verification email or use the original Supabase verification link.";
  }
  if (raw.includes("CONFIRMATION_LINK_TRACKING_DESTINATION_NOT_ALLOWED")) {
    return "The email tracking link did not resolve to the expected RUNYI Supabase verification endpoint.";
  }
  if (
    raw.includes("CONFIRMATION_LINK_TRACKING_REDIRECT_REQUIRED")
    || raw.includes("CONFIRMATION_LINK_TRACKING_REDIRECT_MISSING")
    || raw.includes("CONFIRMATION_LINK_TRACKING_REDIRECT_INVALID")
    || raw.includes("CONFIRMATION_LINK_TRACKING_REDIRECT_LIMIT")
  ) {
    return "The email tracking link could not be resolved safely. Send a fresh verification email and paste the complete link again.";
  }
  if (raw.includes("CONFIRMATION_LINK_TARGET_MISMATCH")) {
    return "This verification link belongs to a different Supabase project or endpoint.";
  }
  if (raw.includes("CONFIRMATION_LINK_TOKEN_INVALID")) {
    return "The verification link is incomplete or no longer valid. Send a fresh verification email and paste the complete link.";
  }
  if (raw.includes("CENTRAL_ACCOUNT_BIND")) {
    return "RUNYI verified the email identity but could not complete the governed account binding. Refresh and retry with a fresh verification email.";
  }
  if (raw === "Edge Function returned a non-2xx status code") {
    return "RUNYI verification was rejected. Send a fresh verification email and try the complete code or link again.";
  }

  return raw;
}

type Catalogue = {
  authorized: boolean;
  reason?: string;
  actor?: {
    actor_user_id?: string;
    actor_email?: string;
    actor_role?: "OWNER_ADMIN" | "ADMIN";
    scope_mode?: string;
    permissions?: Record<string, boolean>;
  };
  active_session?: SetupSession | null;
  accounts?: SetupAccount[];
};

const filters = [
  ["ALL", "All"],
  ["NEEDS_SETUP", "Needs setup"],
  ["OTP_PENDING", "OTP pending"],
  ["SETUP_IN_PROGRESS", "In progress"],
  ["ACTIVE", "Active"],
  ["FAILED", "Failed"],
  ["SUSPENDED", "Suspended"],
  ["MEMBER", "Members"],
  ["ADMIN", "Admins"],
] as const;

type VerificationMode = "AUTO" | "CODE" | "LINK";

function VerificationModeSwitch({
  value,
  onChange,
}: {
  value: VerificationMode;
  onChange: (value: VerificationMode) => void;
}) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-1 rounded-lg bg-background/55 p-1">
      {(["AUTO", "CODE", "LINK"] as VerificationMode[]).map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => onChange(mode)}
          className={`h-8 rounded-md px-2 text-[10px] font-semibold transition ${
            value === mode
              ? "bg-foreground text-background shadow-sm"
              : "text-foreground/52 hover:bg-foreground/[0.06]"
          }`}
        >
          {mode === "AUTO" ? "Auto" : mode === "CODE" ? "Code" : "Link"}
        </button>
      ))}
    </div>
  );
}

function stateBadge(state: string) {
  if (state === "ACTIVE") return "bg-emerald-400/10 text-emerald-300";
  if (state === "SETUP_FAILED") return "bg-red-400/10 text-red-300";
  if (state === "SUSPENDED") return "bg-amber-400/10 text-amber-200";
  if (state === "OTP_PENDING") return "bg-sky-400/10 text-sky-300";
  return "bg-foreground/8 text-foreground/65";
}

function onboardingEvidence(account: SetupAccount) {
  const missing: string[] = [];
  if (!account.central_otp_verified_at) missing.push("RUNYI OTP");
  if (account.project_ref && !account.member_otp_verified_at) missing.push("Member OTP");
  if (account.project_ref && account.project_status !== "VERIFIED") missing.push("Project verification");
  if (String(account.first_use_validation?.status ?? "").toUpperCase() !== "PASS") missing.push("First-use test");
  if (!account.activation_completed_at) missing.push("Activation completion");
  return { complete: missing.length === 0, missing };
}

export function AccountSetupPanel({
  supabase,
  actorEmail,
  onLogout,
}: {
  supabase: SupabaseClient | null;
  actorEmail?: string | null;
  onLogout: () => Promise<void> | void;
}) {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [centralCode, setCentralCode] = useState("");
  const [memberCode, setMemberCode] = useState("");
  const [centralLink, setCentralLink] = useState("");
  const [memberLink, setMemberLink] = useState("");
  const [centralAutoValue, setCentralAutoValue] = useState("");
  const [memberAutoValue, setMemberAutoValue] = useState("");
  const [centralVerificationMode, setCentralVerificationMode] = useState<VerificationMode>("AUTO");
  const [memberVerificationMode, setMemberVerificationMode] = useState<VerificationMode>("AUTO");
  const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
  const [supportReason, setSupportReason] = useState("");

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc("runyi_account_setup_catalogue", {
      p_search: search,
      p_filter: filter,
    });
    if (error) {
      setNotice(error.message);
    } else {
      setCatalogue((data ?? null) as Catalogue | null);
    }
    setLoading(false);
  }, [supabase, search, filter]);

  const loadSupport = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: { operation: "support_status" },
    });
    if (!error && !data?.error) {
      setSupportSession((data?.support ?? null) as SupportSession | null);
    }
  }, [supabase]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 80);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    void loadSupport();
  }, [loadSupport]);

  async function invokeSetupMutation(payload: Record<string, unknown>) {
    if (!supabase) return "RUNYI account setup is unavailable.";
    const { data, error } = await supabase.functions.invoke("runyi-account-setup", {
      body: payload,
    });
    if (error || data?.error) return await readAccountSetupError(error, data);
    return null;
  }

  async function startSupportSession() {
    if (!supabase || !activeSession) return;
    const reason = supportReason.trim();
    if (!reason) {
      setNotice("Enter a support-session reason first.");
      return;
    }

    setLoading(true);
    setNotice(null);
    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "support_start",
        target_email: activeSession.target_email,
        reason,
        support_mode: "SETUP",
        ttl_minutes: 30,
      },
    });

    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) {
      setNotice(errorMessage);
    } else {
      setSupportSession((data?.support ?? null) as SupportSession | null);
      setSupportReason("");
      setNotice(`Governed support session opened for ${activeSession.target_email}.`);
    }
    setLoading(false);
  }

  async function closeSupportSession() {
    if (!supabase || !supportSession?.support_session_id) return;
    setLoading(true);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "support_close",
        support_session_id: supportSession.support_session_id,
        reason: "ADMIN_CLOSED_FROM_ACCOUNT_SETUP",
      },
    });

    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) {
      setNotice(errorMessage);
    } else {
      setSupportSession({ active: false });
      setNotice("Governed support session closed.");
    }
    setLoading(false);
  }

  const activeSession = catalogue?.active_session ?? null;
  const activeAccount = useMemo(
    () => (catalogue?.accounts ?? []).find((account) => account.email === activeSession?.target_email) ?? null,
    [catalogue?.accounts, activeSession?.target_email],
  );

  if (catalogue && catalogue.authorized === false) return null;

  async function startSetup(email: string) {
    if (!supabase) return;
    setLoading(true);
    setNotice(null);
    setCentralCode("");
    setMemberCode("");
    setCentralLink("");
    setMemberLink("");
    setCentralAutoValue("");
    setMemberAutoValue("");
    const errorMessage = await invokeSetupMutation({
      operation: "start_setup",
      target_email: email,
    });
    setNotice(errorMessage ?? `Setup session opened for ${email}.`);
    if (!errorMessage) {
      await load();
      document.getElementById("runyi-active-account-setup")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setLoading(false);
  }

  async function closeSession(action: "RETURN" | "SWITCH" | "LOGOUT") {
    if (!supabase || !activeSession) return;
    setLoading(true);
    setNotice(null);
    const errorMessage = await invokeSetupMutation({
      operation: "close_setup",
      setup_session_id: activeSession.setup_session_id,
      action,
    });
    if (errorMessage) {
      setNotice(errorMessage);
      setLoading(false);
      return;
    }

    setCentralCode("");
    setMemberCode("");
    setCentralLink("");
    setMemberLink("");
    setCentralAutoValue("");
    setMemberAutoValue("");
    if (action === "LOGOUT") {
      await onLogout();
      return;
    }
    setNotice(action === "SWITCH" ? "Setup target cleared. Choose another account." : "Returned to the administrator dashboard.");
    await load();
    setLoading(false);
  }

  async function edge(
    operation: string,
    payload: Record<string, unknown>,
    pendingNotice: string | null = null,
  ) {
    if (!supabase || !activeSession) return null;
    setLoading(true);
    setNotice(pendingNotice);
    const { data, error } = await supabase.functions.invoke("runyi-account-setup", {
      body: {
        operation,
        setup_session_id: activeSession.setup_session_id,
        ...payload,
      },
    });
    if (error || data?.error) {
      setNotice(await readAccountSetupError(error, data));
      setLoading(false);
      return null;
    }
    await load();
    setLoading(false);
    return data;
  }

  async function sendVerificationEmail(phase: "CENTRAL" | "MEMBER") {
    const mode = phase === "CENTRAL" ? centralVerificationMode : memberVerificationMode;
    const result = await edge("send_otp", { phase, verification_mode: mode });
    if (result) {
      setNotice(
        `${phase === "CENTRAL" ? "RUNYI" : "Member-project"} verification email sent to ${activeSession?.target_email}. Use the Code or Link option that matches the email you received.`,
      );
    }
  }

  async function verifyOtp(phase: "CENTRAL" | "MEMBER") {
    const code = phase === "CENTRAL" ? centralCode : memberCode;
    if (code.replace(/\D/g, "").length < 6) {
      setNotice("Enter the complete email code first.");
      return;
    }
    const result = await edge("verify_otp", { phase, code });
    if (result) {
      if (phase === "CENTRAL") setCentralCode("");
      else setMemberCode("");
      setNotice(`${phase === "CENTRAL" ? "RUNYI" : "Member-project"} code verified for ${activeSession?.target_email}.`);
    }
  }

  async function verifyConfirmationLink(phase: "CENTRAL" | "MEMBER") {
    const rawLink = (phase === "CENTRAL" ? centralLink : memberLink).trim();
    if (!/^https:\/\/\S+$/i.test(rawLink)) {
      setNotice("Paste the complete https:// verification link from the email.");
      return;
    }
    const result = await edge(
      "verify_confirmation_link",
      {
        phase,
        confirmation_link: rawLink,
      },
      "Resolving the secure email link and checking the final Supabase verification destination…",
    );
    if (result) {
      if (phase === "CENTRAL") setCentralLink("");
      else setMemberLink("");
      setNotice(`${phase === "CENTRAL" ? "RUNYI" : "Member-project"} link verified for ${activeSession?.target_email}.`);
    }
  }

  async function verifyAuto(phase: "CENTRAL" | "MEMBER") {
    const rawValue = (phase === "CENTRAL" ? centralAutoValue : memberAutoValue).trim();
    if (/^https:\/\/\S+$/i.test(rawValue)) {
      const result = await edge(
        "verify_confirmation_link",
        {
          phase,
          confirmation_link: rawValue,
        },
        "Resolving the secure email link and checking the final Supabase verification destination…",
      );
      if (result) {
        if (phase === "CENTRAL") setCentralAutoValue("");
        else setMemberAutoValue("");
        setNotice(`${phase === "CENTRAL" ? "RUNYI" : "Member-project"} link verified for ${activeSession?.target_email}.`);
      }
      return;
    }

    const code = rawValue.replace(/\D/g, "");
    if (code.length < 6 || code.length > 8) {
      setNotice("Paste the complete verification link or enter the complete 6- to 8-digit email code.");
      return;
    }
    const result = await edge("verify_otp", { phase, code });
    if (result) {
      if (phase === "CENTRAL") setCentralAutoValue("");
      else setMemberAutoValue("");
      setNotice(`${phase === "CENTRAL" ? "RUNYI" : "Member-project"} code verified for ${activeSession?.target_email}.`);
    }
  }

  async function runFirstUseTest() {
    const result = await edge("first_use_test", {});
    if (result?.validation?.pass) {
      setNotice("First-use validation passed. The account is ready to finish activation.");
    } else if (result) {
      setNotice("First-use validation did not pass. Review the validation result before activation.");
    }
  }

  async function finishActivation() {
    if (!supabase || !activeSession) return;
    setLoading(true);
    setNotice(null);
    const errorMessage = await invokeSetupMutation({
      operation: "finish_activation",
      setup_session_id: activeSession.setup_session_id,
    });
    setNotice(errorMessage ?? `${activeSession.target_email} is fully activated.`);
    if (!errorMessage) {
      setCentralCode("");
      setMemberCode("");
      await load();
    }
    setLoading(false);
  }

  async function setSuspended(account: SetupAccount, suspended: boolean) {
    if (!supabase) return;
    setLoading(true);
    setNotice(null);
    const errorMessage = await invokeSetupMutation({
      operation: "set_suspended",
      target_email: account.email,
      suspended,
    });
    setNotice(errorMessage ?? `${account.email} is now ${suspended ? "suspended" : "active"}.`);
    if (!errorMessage) await load();
    setLoading(false);
  }

  return (
    <Card className="min-w-0 overflow-visible border-0 bg-transparent py-0 shadow-none">
      <CardContent className="min-w-0 space-y-3 p-0">
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
            <UserRoundCog className="size-3" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Account Setup & First-Time Activation</p>
            <p className="mt-1 text-xs leading-5 text-foreground/42">
              Switch into governed setup sessions, verify email by code or link, validate member routing and finish first-time activation without replacing your administrator identity.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge className="bg-foreground/8 text-foreground/65">
                {catalogue?.actor?.actor_role === "OWNER_ADMIN" ? "Owner Admin" : "Setup Admin"}
              </Badge>
              <Badge className="bg-foreground/8 text-foreground/65">
                {catalogue?.actor?.scope_mode === "ALL_NON_OWNER" ? "All non-owner accounts" : "Assigned accounts"}
              </Badge>
            </div>
          </div>
        </div>

        {activeSession && (
          <div id="runyi-active-account-setup" className="min-w-0 rounded-2xl bg-primary/[0.035] p-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
                <KeyRound className="size-3" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-foreground/42">Signed in as</p>
                <p className="truncate text-sm font-semibold">{catalogue?.actor?.actor_email ?? actorEmail ?? "Administrator"}</p>
                <p className="mt-2 text-xs text-foreground/42">Setting up</p>
                <p className="break-all text-sm font-semibold text-primary" title={activeSession.target_email}>
                  {activeSession.target_email}
                </p>
                {activeSession.target_project_ref && (
                  <p className="mt-1 truncate font-mono text-[10px] text-foreground/38">{activeSession.target_project_ref}</p>
                )}
              </div>
              <Badge className={stateBadge(activeAccount?.activation_state ?? "SETUP_IN_PROGRESS")}>
                {activeAccount?.activation_state ?? "SETUP_IN_PROGRESS"}
              </Badge>

              {activeAccount && activeAccount.role !== "OWNER_ADMIN" && activeAccount.activation_state === "ACTIVE" && !onboardingEvidence(activeAccount).complete && (
                <div className="col-span-full mt-2 rounded-xl border border-amber-300/15 bg-amber-300/8 p-2.5 text-[11px] leading-5 text-amber-100">
                  Enterprise readiness needs fresh onboarding evidence for this active account. Re-run the RUNYI OTP, member-project OTP, first-use validation, then finish activation. The account stays active during re-verification.
                </div>
              )}
            </div>

            <div className="mt-3 rounded-xl bg-foreground/[0.025] p-2.5">
              {supportSession?.active ? (
                <div className="space-y-2">
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-primary">Governed support session active</p>
                      <p className="mt-0.5 truncate text-[10px] text-foreground/42">
                        {supportSession.target_email} · {supportSession.support_mode ?? "SETUP"}
                      </p>
                      {supportSession.reason && (
                        <p className="mt-1 text-[10px] leading-4 text-foreground/45">{supportSession.reason}</p>
                      )}
                      <p className="mt-1 text-[10px] leading-4 text-foreground/35">
                        Your administrator identity remains the actor. Member passwords are never copied.
                      </p>
                    </div>
                    <Badge className="bg-emerald-400/10 text-emerald-500 dark:text-emerald-300">SUPPORT</Badge>
                  </div>
                  <Button
                    variant="outline"
                    disabled={loading}
                    onClick={() => void closeSupportSession()}
                    className="h-8 w-full border-foreground/12 bg-transparent text-[11px]"
                  >
                    Close support session
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-semibold">Governed support session</p>
                    <p className="mt-0.5 text-[10px] leading-4 text-foreground/42">
                      Open a 30-minute support context for this account while keeping every action attributed to the administrator.
                    </p>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                    <Input
                      value={supportReason}
                      onChange={(event) => setSupportReason(event.target.value)}
                      placeholder="Reason for support access"
                      className="h-9 min-w-0 border-0 bg-background/70 text-xs shadow-none placeholder:text-[10px] placeholder:text-foreground/28"
                    />
                    <Button
                      disabled={loading || !supportReason.trim()}
                      onClick={() => void startSupportSession()}
                      className="h-9 shrink-0 px-3 text-[11px]"
                    >
                      Open support
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 grid gap-2">
              <div className="rounded-xl bg-foreground/[0.025] p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold">RUNYI central email verification</p>
                    <p className="mt-0.5 text-[10px] text-foreground/42">
                      {activeAccount?.central_otp_verified_at
                        ? activeAccount?.setup_stage === "CENTRAL_LINK_VERIFIED" ? "LINK VERIFIED" : "VERIFIED"
                        : activeAccount?.central_otp_sent_at
                          ? centralVerificationMode === "LINK" ? "LINK PENDING" : "EMAIL SENT · CODE OR LINK PENDING"
                          : "Not sent"}
                    </p>
                  </div>
                  {activeAccount?.central_otp_verified_at && <CheckCircle2 className="size-4 text-emerald-300" />}
                </div>
                {!activeAccount?.central_otp_verified_at && (
                  <>
                    <div className="mt-2">
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-foreground/38">
                        Account email
                      </p>
                      <div
                        role="textbox"
                        aria-readonly="true"
                        aria-label="RUNYI verification target email"
                        className="min-h-9 w-full break-all rounded-lg bg-background/70 px-3 py-2 text-xs font-medium leading-5 text-foreground/82"
                        title={activeSession.target_email}
                      >
                        {activeSession.target_email}
                      </div>
                    </div>

                    <VerificationModeSwitch
                      value={centralVerificationMode}
                      onChange={setCentralVerificationMode}
                    />

                    <Button
                      variant="outline"
                      disabled={loading}
                      onClick={() => void sendVerificationEmail("CENTRAL")}
                      className="mt-2 h-9 w-full border-foreground/12 bg-transparent text-xs"
                    >
                      Send verification email
                    </Button>

                    {centralVerificationMode === "AUTO" && (
                      <div className="mt-2 flex min-w-0 gap-2">
                        <Input
                          value={centralAutoValue}
                          onChange={(event) => setCentralAutoValue(event.target.value.slice(0, 8192))}
                          autoComplete="one-time-code"
                          placeholder="Paste code or complete link"
                          className="h-9 min-w-0 border-0 bg-background/70 text-[13px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                        />
                        <Button
                          disabled={loading || !centralAutoValue.trim()}
                          onClick={() => void verifyAuto("CENTRAL")}
                          className="h-9 shrink-0 px-3 text-xs"
                        >
                          Verify
                        </Button>
                      </div>
                    )}

                    {centralVerificationMode === "CODE" && (
                      <div className="mt-2 flex min-w-0 gap-2">
                        <Input
                          value={centralCode}
                          onChange={(event) => setCentralCode(event.target.value.replace(/\D/g, "").slice(0, 8))}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="Email code"
                          className="h-9 min-w-0 border-0 bg-background/70 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                        />
                        <Button
                          disabled={loading || centralCode.length < 6}
                          onClick={() => void verifyOtp("CENTRAL")}
                          className="h-9 shrink-0 px-3 text-xs"
                        >
                          Verify code
                        </Button>
                      </div>
                    )}

                    {centralVerificationMode === "LINK" && (
                      <div className="mt-2 grid gap-2">
                        <Input
                          value={centralLink}
                          onChange={(event) => setCentralLink(event.target.value.slice(0, 8192))}
                          inputMode="url"
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="Paste complete https:// email link"
                          className="h-9 min-w-0 border-0 bg-background/70 text-xs shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                        />
                        <Button
                          disabled={loading || !centralLink.trim()}
                          onClick={() => void verifyConfirmationLink("CENTRAL")}
                          className="h-9 w-full text-xs"
                        >
                          Verify link
                        </Button>
                      </div>
                    )}

                    <p className="mt-2 text-[10px] leading-4 text-foreground/38">
                      Auto accepts either the email code, the direct Supabase link, or an approved tracked email link. RUNYI resolves tracking redirects only to the expected Supabase verification endpoint; links are not retained.
                    </p>
                  </>
                )}
              </div>

              {activeSession.target_project_ref && (
                <div className="rounded-xl bg-foreground/[0.025] p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold">Member-project email verification</p>
                      <p className="mt-0.5 text-[10px] text-foreground/42">
                        {activeAccount?.member_otp_verified_at
                          ? activeAccount?.setup_stage === "MEMBER_LINK_VERIFIED" ? "LINK VERIFIED & ENROLLED" : "VERIFIED & ENROLLED"
                          : activeAccount?.member_otp_sent_at
                            ? memberVerificationMode === "LINK" ? "LINK PENDING" : "EMAIL SENT · CODE OR LINK PENDING"
                            : "Required for member database"}
                      </p>
                    </div>
                    {activeAccount?.member_otp_verified_at && <CheckCircle2 className="size-4 text-emerald-300" />}
                  </div>
                  {!activeAccount?.member_otp_verified_at && (
                    <>
                      <VerificationModeSwitch
                        value={memberVerificationMode}
                        onChange={setMemberVerificationMode}
                      />
                      <Button
                        variant="outline"
                        disabled={loading || !activeAccount?.central_otp_verified_at}
                        onClick={() => void sendVerificationEmail("MEMBER")}
                        className="mt-2 h-9 w-full border-foreground/12 bg-transparent text-xs"
                      >
                        Send member verification email
                      </Button>

                      {memberVerificationMode === "AUTO" && (
                        <div className="mt-2 flex min-w-0 gap-2">
                          <Input
                            value={memberAutoValue}
                            onChange={(event) => setMemberAutoValue(event.target.value.slice(0, 8192))}
                            autoComplete="one-time-code"
                            placeholder="Paste code or complete link"
                            className="h-9 min-w-0 border-0 bg-background/70 text-[13px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                          />
                          <Button
                            disabled={loading || !memberAutoValue.trim()}
                            onClick={() => void verifyAuto("MEMBER")}
                            className="h-9 shrink-0 px-3 text-xs"
                          >
                            Verify
                          </Button>
                        </div>
                      )}

                      {memberVerificationMode === "CODE" && (
                        <div className="mt-2 flex min-w-0 gap-2">
                          <Input
                            value={memberCode}
                            onChange={(event) => setMemberCode(event.target.value.replace(/\D/g, "").slice(0, 8))}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="Member code"
                            className="h-9 min-w-0 border-0 bg-background/70 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                          />
                          <Button
                            disabled={loading || memberCode.length < 6}
                            onClick={() => void verifyOtp("MEMBER")}
                            className="h-9 shrink-0 px-3 text-xs"
                          >
                            Verify code
                          </Button>
                        </div>
                      )}

                      {memberVerificationMode === "LINK" && (
                        <div className="mt-2 grid gap-2">
                          <Input
                            value={memberLink}
                            onChange={(event) => setMemberLink(event.target.value.slice(0, 8192))}
                            inputMode="url"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="Paste complete https:// email link"
                            className="h-9 min-w-0 border-0 bg-background/70 text-xs shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
                          />
                          <Button
                            disabled={loading || !memberLink.trim()}
                            onClick={() => void verifyConfirmationLink("MEMBER")}
                            className="h-9 w-full text-xs"
                          >
                            Verify link
                          </Button>
                        </div>
                      )}

                      <p className="mt-2 text-[10px] leading-4 text-foreground/38">
                        Code mode requires a numeric-code email template. Link and Auto work with the provider&apos;s normal magic-link template.
                      </p>
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  disabled={loading || !activeAccount?.central_otp_verified_at || Boolean(activeSession.target_project_ref && !activeAccount?.member_otp_verified_at)}
                  onClick={() => void runFirstUseTest()}
                  className="h-9 min-w-0 border-foreground/12 bg-transparent px-2 text-xs"
                >
                  Test first use
                </Button>
                <Button
                  disabled={loading || activeAccount?.first_use_validation?.status !== "PASS"}
                  onClick={() => void finishActivation()}
                  className="h-9 min-w-0 px-2 text-xs"
                >
                  Finish activation
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => void closeSession("SWITCH")}
                  className="h-auto min-h-9 min-w-0 whitespace-normal border-foreground/12 bg-transparent px-2 py-2 text-center text-[11px] leading-4"
                >
                  Switch account
                </Button>
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => void closeSession("RETURN")}
                  className="h-auto min-h-9 min-w-0 whitespace-normal border-foreground/12 bg-transparent px-2 py-2 text-center text-[11px] leading-4"
                >
                  Return to Admin
                </Button>
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => void closeSession("LOGOUT")}
                  className="col-span-2 h-auto min-h-9 min-w-0 border-foreground/12 bg-transparent px-2 py-2 text-[11px]"
                >
                  <LogOut className="mr-1 size-3" />
                  Log out
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_160px]">
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3 -translate-y-1/2 text-foreground/30" />
            <Input
              type="search"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search email, UUID, project, role or status…"
              className="h-9 w-full min-w-0 border-0 bg-foreground/[0.035] pl-8 text-[15px] shadow-none placeholder:text-[11px] placeholder:text-foreground/28 focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-full min-w-0 border-0 bg-foreground/[0.035] text-xs shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filters.map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between gap-2 text-[11px] text-foreground/42">
          <span>Email · UUID · project ref · role · stage · letters · numbers · symbols</span>
          <span className="shrink-0">{catalogue?.accounts?.length ?? 0}</span>
        </div>

        {notice && (
          <div className="min-w-0 break-words rounded-xl border border-amber-300/20 bg-amber-300/8 p-3 text-xs leading-5 text-amber-100">
            {notice}
          </div>
        )}

        <div className="space-y-2">
          {(catalogue?.accounts ?? []).map((account) => (
            <div
              key={account.email}
              className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-xl bg-foreground/[0.025] px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="truncate text-sm font-semibold" title={account.email}>{account.email}</p>
                  <Badge className={`shrink-0 text-[9px] ${stateBadge(account.activation_state)}`}>
                    {account.activation_state}
                  </Badge>
                </div>
                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px] text-foreground/42">
                  <span>{account.role}</span>
                  <span>·</span>
                  <span>{account.setup_stage}</span>
                  {account.project_ref && (
                    <>
                      <span>·</span>
                      <span className="max-w-[10rem] truncate font-mono" title={account.project_ref}>{account.project_ref}</span>
                    </>
                  )}
                </div>
                {account.role !== "OWNER_ADMIN" && account.activation_state === "ACTIVE" && (() => {
                  const evidence = onboardingEvidence(account);
                  return evidence.complete ? (
                    <p className="mt-1 text-[10px] text-emerald-300">Enterprise onboarding evidence verified</p>
                  ) : (
                    <p className="mt-1 text-[10px] text-amber-200" title={evidence.missing.join(", ")}>
                      Re-verification required · {evidence.missing.join(" · ")}
                    </p>
                  );
                })()}
                {account.last_failure && (
                  <p className="mt-1 truncate text-[10px] text-red-300" title={account.last_failure}>
                    {account.last_failure}
                  </p>
                )}
              </div>

              <details className="relative shrink-0">
                <summary
                  className="grid size-7 cursor-pointer list-none place-items-center rounded-lg bg-transparent text-foreground/55 hover:bg-foreground/[0.06] [&::-webkit-details-marker]:hidden"
                  aria-label={`Open setup actions for ${account.email}`}
                >
                  <MoreVertical className="size-3" />
                </summary>
                <div className="absolute right-0 top-8 z-50 w-52 overflow-hidden rounded-xl bg-popover p-1.5 shadow-2xl shadow-black/25">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={(event) => {
                      event.currentTarget.closest("details")?.removeAttribute("open");
                      void startSetup(account.email);
                    }}
                    className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                  >
                    {account.activation_state === "ACTIVE"
                      ? onboardingEvidence(account).complete
                        ? "Open setup / test"
                        : "Re-verify onboarding"
                      : "Start / continue setup"}
                  </button>
                  {account.role !== "OWNER_ADMIN" && (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={(event) => {
                        event.currentTarget.closest("details")?.removeAttribute("open");
                        void setSuspended(account, account.activation_state !== "SUSPENDED");
                      }}
                      className="flex h-9 w-full items-center rounded-lg px-2.5 text-left text-xs text-foreground/75 hover:bg-foreground/8 disabled:opacity-40"
                    >
                      {account.activation_state === "SUSPENDED" ? "Reactivate account" : "Suspend account"}
                    </button>
                  )}
                </div>
              </details>
            </div>
          ))}

          {!loading && (catalogue?.accounts ?? []).length === 0 && (
            <div className="rounded-xl bg-foreground/[0.025] p-4 text-center text-xs text-foreground/45">
              No accounts match this search or filter.
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-foreground/45">
            <LoaderCircle className="size-3.5 animate-spin" />
            Updating account setup…
          </div>
        )}

        <div className="flex items-start gap-2 rounded-xl bg-foreground/[0.018] p-2.5">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-emerald-300" />
          <p className="text-[11px] leading-5 text-foreground/45">
            Setup sessions preserve the real administrator identity. OTP values are never stored, OWNER_ADMIN cannot be delegated through setup, and switching targets closes the previous setup context.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
